/**
 * kHtmlSafeValueCore - the attribute / CSS value checks of kHtmlSafeCore (numbers, lengths, paints, ids, transforms, paths).
 * Split out of kHtmlSafeCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). kHtmlSafeCore.ts
 * re-exports every public name, so importers of "./kHtmlSafeCore" are unchanged.
 */

import { getCoreUtils } from "./kCoreHandler";
import { kHtmlParserCore } from "./kHtmlParserCore";
import type { kHtmlSafePolicy } from "./kHtmlSafeCore";
import { kHtmlSafeAllowList } from "./kHtmlSafeAllowList";


/** Value checks, one per attribute KIND. Each answers the value to keep, or null to drop it. */
export class kHtmlSafeValueCore {
    public static MaxNumber: number = 100000;
    private static NumberPattern: RegExp = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d{1,3})?$/;
    private static IdPattern: RegExp = /^[A-Za-z][\w-]{0,63}$/;
    private static UrlIdPattern: RegExp = /^url\(#([A-Za-z][\w-]{0,63})\)$/;
    private static PathPattern: RegExp = /^[MmLlHhVvCcSsQqTtAaZz0-9eE+\-.,\s]*$/;
    private static HexPattern: RegExp = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
    private static AspectPattern: RegExp = /^(?:none|x(?:Min|Mid|Max)Y(?:Min|Mid|Max))(?: (?:meet|slice))?$/;
    private static TransformStep: RegExp = /\s*(translate|scale|rotate|matrix|skewX|skewY)\s*\(([^()]*)\)\s*,?/y;

    /** The value to keep for an attribute of this Kind, or null to drop it. */
    public static Check = (Kind: string, Value: string, Policy: kHtmlSafePolicy): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const V: string = Value.trim();
            if (Kind.indexOf("enum:") === 0) { return kHtmlSafeValueCore.EnumOf(Kind.substring(5), V); }
            if (Kind === "text") { return kHtmlSafeValueCore.TextOf(Value, Policy); }
            if (Kind === "class") { return kHtmlSafeValueCore.ClassOf(V, Policy); }
            if (Kind === "id") { return kHtmlSafeValueCore.IdOf(V, Policy); }
            if (Kind === "clipref") { return kHtmlSafeValueCore.UrlRefOf(V, Policy); }
            if (Kind === "paint") { return kHtmlSafeValueCore.PaintOf(V, Policy, true); }
            if (Kind === "color") { return kHtmlSafeValueCore.PaintOf(V, Policy, false); }
            return kHtmlSafeValueCore.ShapeValueOf(Kind, V);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.Check");
            return null;
        }
    };

    /** The numeric / geometric kinds. Unknown kind = null (a policy typo admits nothing). */
    private static ShapeValueOf = (Kind: string, V: string): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            let Ok: boolean = false;
            if (Kind === "span") { Ok = kHtmlSafeValueCore.IsIntegerIn(V, 1, 50); }
            else if (Kind === "int") { Ok = kHtmlSafeValueCore.IsIntegerIn(V, -kHtmlSafeValueCore.MaxNumber, kHtmlSafeValueCore.MaxNumber); }
            else if (Kind === "number") { Ok = kHtmlSafeValueCore.IsNumber(V); }
            else if (Kind === "length") { Ok = kHtmlSafeValueCore.IsLength(V); }
            else if (Kind === "lengths") { Ok = kHtmlSafeValueCore.IsList(V, false, 200); }
            else if (Kind === "dash") { Ok = V === "none" || kHtmlSafeValueCore.IsList(V, false, 32); }
            else if (Kind === "points") { Ok = kHtmlSafeValueCore.IsList(V, true, 10000); }
            else if (Kind === "unit") { Ok = kHtmlSafeValueCore.IsUnitInterval(V); }
            else if (Kind === "viewbox") { Ok = kHtmlSafeValueCore.IsViewBox(V); }
            else if (Kind === "path") { Ok = V !== "" && kHtmlSafeValueCore.PathPattern.test(V); }
            else if (Kind === "transform") { Ok = kHtmlSafeValueCore.IsTransform(V); }
            else if (Kind === "aspect") { Ok = kHtmlSafeValueCore.AspectPattern.test(V); }
            else if (Kind === "svgns") { Ok = V === kHtmlSafeAllowList.SvgNs; }
            if (Ok === true) { return V; }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.ShapeValueOf");
            return null;
        }
    };

    /** A plain number within +-MaxNumber. */
    public static IsNumber = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (V.length > 32 || kHtmlSafeValueCore.NumberPattern.test(V) === false) { return false; }
            const N: number = parseFloat(V);
            return isFinite(N) === true && Math.abs(N) <= kHtmlSafeValueCore.MaxNumber;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsNumber");
            return false;
        }
    };

    /** A number with an optional unit (px, %, em, rem, ex, pt). */
    public static IsLength = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Unit: RegExpExecArray | null = /(px|%|em|rem|ex|pt)$/.exec(V);
            if (Unit === null) { return kHtmlSafeValueCore.IsNumber(V); }
            return kHtmlSafeValueCore.IsNumber(V.substring(0, V.length - Unit[1].length));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsLength");
            return false;
        }
    };

    /** A whole number in [Min, Max]. */
    private static IsIntegerIn = (V: string, Min: number, Max: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (/^[+-]?\d{1,7}$/.test(V) === false) { return false; }
            const N: number = parseInt(V, 10);
            return N >= Min && N <= Max;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsIntegerIn");
            return false;
        }
    };

    /** Numbers (or lengths, when not NumbersOnly) separated by whitespace/commas, 1..MaxItems of them. */
    private static IsList = (V: string, NumbersOnly: boolean, MaxItems: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (V === "") { return false; }
            const Items: string[] = V.split(/[\s,]+/);
            if (Items.length > MaxItems) { return false; }
            for (const Item of Items) {
                if (NumbersOnly === true && kHtmlSafeValueCore.IsNumber(Item) === false) { return false; }
                if (NumbersOnly === false && kHtmlSafeValueCore.IsLength(Item) === false) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsList");
            return false;
        }
    };

    /** 0..1, or 0%..100%. */
    private static IsUnitInterval = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            let Text: string = V;
            let Max: number = 1;
            if (Text.charAt(Text.length - 1) === "%") { Text = Text.substring(0, Text.length - 1); Max = 100; }
            if (kHtmlSafeValueCore.IsNumber(Text) === false) { return false; }
            const N: number = parseFloat(Text);
            return N >= 0 && N <= Max;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsUnitInterval");
            return false;
        }
    };

    /** Four numbers, width and height positive. */
    public static IsViewBox = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Parts: string[] = V.split(/[\s,]+/);
            if (Parts.length !== 4) { return false; }
            for (const Part of Parts) { if (kHtmlSafeValueCore.IsNumber(Part) === false) { return false; } }
            return parseFloat(Parts[2]) > 0 && parseFloat(Parts[3]) > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsViewBox");
            return false;
        }
    };

    /** A transform list: translate/scale/rotate/matrix/skewX/skewY, each with the right count of numbers. */
    public static IsTransform = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (V === "" || V.length > 1000) { return false; }
            const Step: RegExp = new RegExp(kHtmlSafeValueCore.TransformStep.source, "y");
            let Steps: number = 0;
            while (Step.lastIndex < V.length) {
                const Match: RegExpExecArray | null = Step.exec(V);
                if (Match === null) { return V.substring(Step.lastIndex).trim() === "" && Steps > 0; }
                if (kHtmlSafeValueCore.IsTransformArgs(Match[1], Match[2]) === false) { return false; }
                Steps = Steps + 1;
                if (Steps > 16) { return false; }
            }
            return Steps > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsTransform");
            return false;
        }
    };

    /** The arguments of one transform function: all numbers, and as many as that function takes. */
    private static IsTransformArgs = (Name: string, Args: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Trimmed: string = Args.trim();
            if (Trimmed === "") { return false; }
            const Parts: string[] = Trimmed.split(/[\s,]+/);
            for (const Part of Parts) { if (kHtmlSafeValueCore.IsNumber(Part) === false) { return false; } }
            const Count: number = Parts.length;
            if (Name === "matrix") { return Count === 6; }
            if (Name === "rotate") { return Count === 1 || Count === 3; }
            if (Name === "translate" || Name === "scale") { return Count === 1 || Count === 2; }
            return Count === 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsTransformArgs");
            return false;
        }
    };

    /** none | currentColor | transparent | #hex | rgb()/rgba()/hsl()/hsla() of numbers | a named colour
     *  (letters only) | url(#id) when AllowUrl. A url reference gets the policy's id prefix. */
    public static PaintOf = (V: string, Policy: kHtmlSafePolicy, AllowUrl: boolean): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (V.indexOf("url(") === 0) {
                if (AllowUrl === false) { return null; }
                return kHtmlSafeValueCore.UrlRefOf(V, Policy);
            }
            if (kHtmlSafeValueCore.HexPattern.test(V) === true) { return V; }
            if (/^[A-Za-z]{1,30}$/.test(V) === true) { return V; }
            if (kHtmlSafeValueCore.IsColorFunction(V) === true) { return V; }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.PaintOf");
            return null;
        }
    };

    /** rgb()/rgba()/hsl()/hsla() whose 3 or 4 arguments are numbers, optionally % or deg. */
    private static IsColorFunction = (V: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Match: RegExpExecArray | null = /^(?:rgba?|hsla?)\(([^()]{1,80})\)$/.exec(V);
            if (Match === null) { return false; }
            const Parts: string[] = Match[1].trim().split(/[\s,/]+/);
            if (Parts.length < 3 || Parts.length > 4) { return false; }
            for (const Part of Parts) {
                const Bare: string = Part.replace(/(%|deg)$/, "");
                if (kHtmlSafeValueCore.IsNumber(Bare) === false) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsColorFunction");
            return false;
        }
    };

    /** Exactly url(#id), rewritten to url(#<prefix>id). */
    public static UrlRefOf = (V: string, Policy: kHtmlSafePolicy): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Match: RegExpExecArray | null = kHtmlSafeValueCore.UrlIdPattern.exec(V);
            if (Match === null) { return null; }
            return "url(#" + Policy.IdPrefix + Match[1] + ")";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.UrlRefOf");
            return null;
        }
    };

    /** An id of the safe shape, with the policy's prefix. */
    public static IdOf = (V: string, Policy: kHtmlSafePolicy): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (kHtmlSafeValueCore.IdPattern.test(V) === false) { return null; }
            return Policy.IdPrefix + V;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IdOf");
            return null;
        }
    };

    /** Only the policy's class tokens, each once, in source order; null when none is left. */
    public static ClassOf = (V: string, Policy: kHtmlSafePolicy): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Kept: string[] = [];
            for (const Token of V.split(/\s+/)) {
                if (Token === "" || Kept.indexOf(Token) >= 0) { continue; }
                if (Policy.Classes.indexOf(Token) < 0) { continue; }
                Kept.push(Token);
            }
            if (Kept.length === 0) { return null; }
            return Kept.join(" ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.ClassOf");
            return null;
        }
    };

    /** Free text (title, aria-label), at most MaxTextChars. */
    private static TextOf = (V: string, Policy: kHtmlSafePolicy): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (V.length > Policy.MaxTextChars) { return null; }
            return V;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.TextOf");
            return null;
        }
    };

    /** One of the '|'-separated words, exactly. */
    private static EnumOf = (Words: string, V: string): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (V === "" || Words.split("|").indexOf(V) < 0) { return null; }
            return V;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.EnumOf");
            return null;
        }
    };

    /**
     * The value as a hostile renderer might read it: entities decoded (again, and also the numeric
     * forms without ';' and &colon; / &tab; / &newline;), every whitespace, control, zero-width and
     * backslash character removed, lower case. Used ONLY to refuse, never to emit.
     */
    public static Normalised = (Value: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Decoded: string = kHtmlSafeValueCore.DecodeLoose(kHtmlParserCore.DecodeEntities(Value));
            let Out: string = "";
            for (const Ch of Decoded) {
                if (kHtmlSafeValueCore.IsInvisible(Ch.codePointAt(0) || 0) === true) { continue; }
                Out = Out + Ch;
            }
            return Out.toLowerCase();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.Normalised");
            return "";
        }
    };

    /** Characters a URL or CSS parser skips or treats as escapes: C0/C1 controls, space, DEL, soft
     *  hyphen, zero-width and bidi marks, line/paragraph separators, BOM, and backslash. */
    private static IsInvisible = (Code: number): boolean => {
        if (getCoreUtils().KErrorMode) { return true; }
        try {
            if (Code <= 0x20 || (Code >= 0x7F && Code <= 0xA0) || Code === 0xAD || Code === 0x5C) { return true; }
            if (Code >= 0x200B && Code <= 0x200F) { return true; }
            return Code === 0x2028 || Code === 0x2029 || Code === 0xFEFF || Code === 0x180E || Code === 0x3000;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsInvisible");
            return true;
        }
    };

    /** Decodes what DecodeEntities leaves alone: &#106 / &#x6a without ';', &colon; &tab; &newline;. */
    private static DecodeLoose = (Text: string): string => {
        if (getCoreUtils().KErrorMode) { return Text; }
        try {
            if (Text.indexOf("&") < 0) { return Text; }
            const Pattern: RegExp = /&(?:#[xX]([0-9a-fA-F]{1,6})|#([0-9]{1,7})|(colon|tab|newline));?/gi;
            let Out: string = "";
            let Last: number = 0;
            let Match: RegExpExecArray | null = Pattern.exec(Text);
            while (Match !== null) {
                Out = Out + Text.substring(Last, Match.index) + kHtmlSafeValueCore.LooseValue(Match);
                Last = Match.index + Match[0].length;
                Match = Pattern.exec(Text);
            }
            return Out + Text.substring(Last);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.DecodeLoose");
            return Text;
        }
    };

    /** The character one loose reference stands for (a reference out of range stays as written). */
    private static LooseValue = (Match: RegExpExecArray): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Match[3] !== undefined) {
                const Name: string = Match[3].toLowerCase();
                if (Name === "colon") { return ":"; }
                if (Name === "tab") { return "\t"; }
                return "\n";
            }
            let Code: number = parseInt(Match[2], 10);
            if (Match[1] !== undefined) { Code = parseInt(Match[1], 16); }
            if (isFinite(Code) === false || Code < 0 || Code > 0x10FFFF) { return Match[0]; }
            return String.fromCodePoint(Code);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.LooseValue");
            return "";
        }
    };

    /** True when a value must be refused whatever its attribute: a script/data scheme or a url( that
     *  is not exactly url(#id), in any spelling a renderer would decode to that. */
    public static IsHostileValue = (Value: string): boolean => {
        if (getCoreUtils().KErrorMode) { return true; }
        try {
            const Seen: string = kHtmlSafeValueCore.Normalised(Value);
            for (const Part of kHtmlSafeAllowList.DeniedValueParts) {
                if (Seen.indexOf(Part) >= 0) { return true; }
            }
            if (Seen.indexOf("url(") < 0) { return false; }
            return kHtmlSafeValueCore.UrlIdPattern.test(Value.trim()) === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeValueCore.IsHostileValue");
            return true;
        }
    };
}
